/**
 * Safely converts an external string value to a string-valued enum.
 *
 * This helper is intended for mapping external string inputs (route/query params,
 * API payloads, localStorage, etc.) to string-valued enums, providing type safety
 * and handling invalid values gracefully.
 *
 * @param enumObj - The string-valued enum object to match against
 * @param value - The value to convert (can be any type, but must be a string to match)
 * @param defaultValue - Optional default value to return if value doesn't match or isn't a string
 * @returns The matching enum value, or defaultValue if provided, or null if not found
 *
 * @example
 * ```ts
 * // Without default - returns enum value or null
 * const type = toEnumValue(MessageType, route.query.type); // MessageType | null
 *
 * // With default - returns enum value or default (never null)
 * const type = toEnumValue(MessageType, raw, MessageType.sent); // MessageType
 * ```
 */
export function toEnumValue<TEnum extends { [k: string]: string }>(
  enumObj: TEnum,
  value: unknown
): TEnum[keyof TEnum] | null;

export function toEnumValue<TEnum extends { [k: string]: string }>(
  enumObj: TEnum,
  value: unknown,
  defaultValue: TEnum[keyof TEnum]
): TEnum[keyof TEnum];

export function toEnumValue<TEnum extends { [k: string]: string }>(
  enumObj: TEnum,
  value: unknown,
  defaultValue?: TEnum[keyof TEnum]
): TEnum[keyof TEnum] | null {
  if (typeof value !== 'string') {
    return defaultValue ?? null;
  }

  const values = Object.values(enumObj) as string[];
  const match = values.find((v) => v === value);

  if (match !== undefined) {
    return match as TEnum[keyof TEnum];
  }

  return defaultValue ?? null;
}

